import { Api } from '@axiom/ui';
import type {
  Opportunity,
  TimeZoneIanaAll,
  OpportunityInterview,
  Candidate,
  OpportunityOnboarding,
  OpportunityOnboardingPatchBody,
  CandidateReputationDetailsRequestBody,
  CandidateReputationDetailsResponseBody,
} from '@axiom/validation';

import { EnvUtil } from '../utils/env-util';

export type ReadOpportunitiesResponseType = {
  data: Opportunity[];
  meta: {
    currentPage: number;
    pageCount: number;
    resultCount: number;
  };
};

export type ReadOpportunityInterviewsType = {
  data: OpportunityInterview[];
  meta: {
    aggregations: Record<string, unknown> | null;
    currentPage: number;
    pageCount: number;
    resultCount: number;
    searchAfter: [string] | null;
  };
};

class OpportunityApiClass extends Api {
  constructor() {
    super({
      domain: EnvUtil.apiEnvoyUrl,
    });
  }

  readOpportunities({
    filters,
    search,
    sort,
    page = 1,
  }: {
    filters?: Record<string, unknown>;
    search?: string;
    sort?: string;
    page?: number;
  }) {
    return super.read<ReadOpportunitiesResponseType>({
      endpoint: `/opportunities/search`,
      method: 'POST',
      body: {
        filters,
        search,
        sort,
        page,
      },
    });
  }

  readOpportunityInterviews({
    opportunityId,
    body,
  }: {
    opportunityId: Opportunity['id'];
    body: {
      timezone: TimeZoneIanaAll;
      page?: number;
      pageSize?: number;
      sort?: string;
      order?: 'asc' | 'desc';
    };
  }) {
    return super.read<ReadOpportunityInterviewsType>({
      endpoint: `/opportunities/${opportunityId}/interviews`,
      method: 'POST',
      body,
    });
  }

  readOpportunityReputationDetails({
    opportunityId,
    body,
  }: {
    opportunityId: Opportunity['id'];
    body: CandidateReputationDetailsRequestBody;
  }) {
    return super.read<CandidateReputationDetailsResponseBody>({
      endpoint: `/opportunities/${opportunityId}/reputationDetails`,
      method: 'POST',
      body,
    });
  }

  async updatePublishOpportunity({
    opportunityId,
  }: {
    opportunityId: Opportunity['id'];
  }) {
    return super.write<{ data: Opportunity }>({
      endpoint: `/opportunities/${opportunityId}/publishSubmission`,
      method: 'PATCH',
    });
  }

  readOpportunitiesOnboarding({
    opportunityId,
  }: {
    opportunityId: Opportunity['id'];
  }) {
    return super.read<{ data: OpportunityOnboarding }>({
      endpoint: `/opportunities/${opportunityId}/onboarding`,
      method: 'GET',
    });
  }

  async refreshOpportunitiesOnboarding({
    opportunityId,
  }: {
    opportunityId: Opportunity['id'];
  }) {
    return super.invalidate(`/opportunities/${opportunityId}/onboarding`);
  }

  async updateOpportunitiesOnboardingCandidate({
    opportunityId,
    candidateId,
    body,
  }: {
    opportunityId: Opportunity['id'];
    candidateId: Candidate['id'];
    body: Partial<OpportunityOnboardingPatchBody>;
  }) {
    return super.write<{ data: OpportunityOnboarding }>({
      endpoint: `/opportunities/${opportunityId}/onboardingDetails/${candidateId}`,
      method: 'PATCH',
      body,
    });
  }

  async createSendOnboardingEmail({
    opportunityId,
    candidateId,
  }: {
    opportunityId: Opportunity['id'];
    candidateId: Candidate['id'];
  }) {
    return super.write<{ data: Opportunity }>({
      endpoint: `/opportunities/${opportunityId}/sendOnboardingEmail/${candidateId}`,
      method: 'POST',
    });
  }

  async refreshPublishOpportunity({
    opportunityId,
  }: {
    opportunityId: Opportunity['id'];
  }) {
    return super.invalidate(
      `/opportunities/${opportunityId}/publishSubmission`
    );
  }

  async refreshOpportunityReputationDetails({
    opportunityId,
  }: {
    opportunityId: Opportunity['id'];
  }) {
    return super.invalidate(
      `/opportunities/${opportunityId}/reputationDetails`
    );
  }
}

export const OpportunityApi = new OpportunityApiClass();
